<script>
// @ts-check

import { getGeneralJournalReport } from '@/api/reports'
import DateRangeField from '@/components/fields/DateRangeField.vue'
import FileViewer from '@/components/file/FileViewer.vue'
import { demoMixin } from '@/core/mixins/demo'
import { formattedPeriod } from '@/helpers/formatters'
import { downloadFile } from '@/helpers/functions'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'GeneralJournal',
  components: {
    FileViewer,
    DateRangeField
  },
  mixins: [demoMixin],
  data: () => ({
    accountingPeriodId: null,
    periodData: null,
    report: null,
    isLoadingPeriods: true,
    isLoadingReport: false,
    dateRange: [null, null]
  }),
  computed: {
    ...mapGetters('reports', ['accountingPeriods']),
    ...mapGetters('mobileData', ['isMobileWrapperUser']),
    formattedAccountingPeriodOptions() {
      return this.accountingPeriods.map(formattedPeriod)
    },
    startDate() {
      return this.dateRange[0]
    },
    endDate() {
      return this.dateRange[1]
    }
  },
  watch: {
    accountingPeriodId(newPeriod) {
      if (!newPeriod) {
        return
      }

      this.periodData = this.accountingPeriods.find(
        ({ id }) => `${id}` === `${this.accountingPeriodId}`
      )

      this.dateRange = [
        this.periodData.period_start,
        this.periodData.period_end
      ]
    },
    async dateRange() {
      if (this.startDate && this.endDate) {
        await this.skipInDemoMode(this.getGeneralJournalReport)
      } else {
        this.report = null
      }

      if (
        this.periodData &&
        (this.periodData.period_start !== this.startDate ||
          this.periodData.period_end !== this.endDate)
      ) {
        this.accountingPeriodId = null
        this.periodData = null
      }
    }
  },
  async created() {
    await this.skipInDemoMode(this.fetchAccountingPeriods)
    this.isLoadingPeriods = false
  },
  methods: {
    ...mapActions('reports', ['getAccountingPeriods']),
    async fetchAccountingPeriods() {
      try {
        await this.getAccountingPeriods()

        const accountingPeriod = this.accountingPeriods.find(
          ({ current }) => current === true
        )

        if (accountingPeriod) {
          this.accountingPeriodId = accountingPeriod.id
        }
      } catch (error) {
        console.error(error)
      }
    },
    downloadReport() {
      if (!this.report) {
        return
      }

      downloadFile(
        this.report.file,
        `${this.startDate.replace(/-/g, '')}_general_journal.pdf`,
        'application/pdf',
        this.isMobileWrapperUser
      )
    },
    async getGeneralJournalReport() {
      this.report = null
      this.isLoadingReport = true

      try {
        this.report = await getGeneralJournalReport(
          this.startDate,
          this.endDate
        )
      } catch (error) {
        console.error(error)
      }

      this.isLoadingReport = false
    }
  }
}
</script>

<template>
  <div v-if="isLoadingPeriods" class="my-4 flex items-center justify-center">
    <p>{{ $t('reports.loading_account_periods') }}</p>

    <ProgressCircular class="ml-2" />
  </div>

  <div v-else>
    <p class="mb-4">
      {{ $t('reports.only_shows_accepted_entries') }}
    </p>

    <div class="grid-controls xl:grid-cols-2">
      <VSelect
        v-model="accountingPeriodId"
        :label="$t('form.label.accounting_period')"
        :placeholder="$t('reports.choose_accounting_period')"
        :items="formattedAccountingPeriodOptions"
        item-title="label"
        item-value="value"
      />
      <DateRangeField
        v-model="dateRange"
        :label="$t('reports.get_daterange')"
      />
    </div>

    <FileViewer
      v-if="report && !isInDemoMode"
      class="my-4 w-full"
      :file="report"
    />

    <div
      v-else-if="isLoadingReport"
      class="my-8 flex items-center justify-center"
    >
      <p>{{ $t('reports.loading_general_journal') }}</p>
      <ProgressCircular class="ml-2" />
    </div>

    <p v-else-if="!isInDemoMode" class="my-8 flex items-center justify-center">
      {{ $t('reports.select_general_journal_date_range') }}
    </p>

    <div v-if="!isInDemoMode" class="mt-2 justify-end sm:flex">
      <PrimaryButton
        v-show="report"
        class="w-full lg:w-auto"
        :text="$t('reports.download_general_journal')"
        @click="downloadReport"
      />
    </div>
  </div>
</template>
